import { Tooltip } from '@mantine/core';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { ppmIconSmall } from 'app/assets/ppm/16/ppmIconSmall';
import { ppmIconLarge } from 'app/assets/ppm/32/ppmIconLarge';

import type { ArticleModelLifeCycleCode } from 'api/models';

interface ArticleLifeCycleIconProps {
    lifeCycleCode: ArticleModelLifeCycleCode | undefined;
}

const lifeCycleTooltipKeys = {
    I: 'Market Introduction',
    G: 'Current Portfolio (Growth)',
    P: 'Phase Out',
    E: 'Eliminated',
    O: 'Exceptional (Occasional) Use',
    N: 'Not used in country',
    NA: 'Not available to country',
    U: 'Undefined'
};

export const ArticleLifeCycleIcon = ({ lifeCycleCode }: ArticleLifeCycleIconProps) => {
    const imgRef = useRef<HTMLImageElement | null>(null);
    const lifeCycleLabel = useMemo(() => lifeCycleTooltipKeys[lifeCycleCode ?? 'U'], [lifeCycleCode]);

    const updateIcon = useCallback(() => {
        if (!imgRef.current) {
            return;
        }

        const dpr = window.devicePixelRatio || 1;
        imgRef.current.src = dpr > 1 ? ppmIconLarge[lifeCycleCode ?? 'U'] : ppmIconSmall[lifeCycleCode ?? 'U'];
    }, [lifeCycleCode]);

    useEffect(() => {
        window.addEventListener('resize', updateIcon);
        return () => window.removeEventListener('resize', updateIcon);
    }, [lifeCycleCode, updateIcon]);

    const handleIconRef = (img: HTMLImageElement | null) => {
        imgRef.current = img;
        updateIcon();
    };

    return (
        <Tooltip label={lifeCycleLabel}>
            <img
                ref={handleIconRef}
                alt={lifeCycleLabel}
                height={'16px'}
                src={ppmIconSmall[lifeCycleCode ?? 'U']}
                width={'16px'}
                onDragStart={(e) => e.preventDefault()}
            />
        </Tooltip>
    );
};
